/**
 * Optimizer Control Center — next-run countdown math (pure)
 * For Media Devoted | Created By Ciro Autiero
 *
 * `nextRunAt = lastRunAt + intervalMin * 60_000`. Precedence mirrors the
 * scheduler candidate rules: STANDARD mode opts out entirely, the kill
 * switch pauses pushes, `enabled=false` means manual-only.
 */

import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';

export type NextRunState = 'auto' | 'due' | 'manual' | 'paused' | 'standard';

export interface NextRunInfo {
  state: NextRunState;
  /** Milliseconds until the next scheduled run; only present for 'auto'/'due'. */
  msLeft?: number;
}

type NextRunRow = Pick<
  OptimizerRow,
  'mode' | 'enabled' | 'killSwitch' | 'intervalMin' | 'lastRunAt'
>;

export function nextRunInfo(row: NextRunRow, nowMs: number): NextRunInfo {
  if (row.mode === 'STANDARD') return { state: 'standard' };
  if (row.killSwitch) return { state: 'paused' };
  if (!row.enabled) return { state: 'manual' };
  if (!row.lastRunAt) return { state: 'due', msLeft: 0 };
  const lastRunMs = new Date(row.lastRunAt).getTime();
  if (Number.isNaN(lastRunMs)) return { state: 'due', msLeft: 0 };
  const msLeft = lastRunMs + row.intervalMin * 60_000 - nowMs;
  if (msLeft <= 0) return { state: 'due', msLeft: 0 };
  return { state: 'auto', msLeft };
}

/** "1h 05m 09s" / "4m 02s" / "32s" for live countdown cells. */
export function formatCountdown(msLeft: number): string {
  const totalSeconds = Math.max(0, Math.ceil(msLeft / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (hours > 0) return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
  if (minutes > 0) return `${minutes}m ${pad(seconds)}s`;
  return `${seconds}s`;
}

/** "15m", "1h", "1h 30m", "24h" for interval labels. */
export function formatIntervalMin(intervalMin: number): string {
  if (intervalMin < 60) return `${intervalMin}m`;
  const hours = Math.floor(intervalMin / 60);
  const minutes = intervalMin % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}
